// ===========================================
// Deletion Section Component
// ===========================================

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  DeleteForever as DeleteIcon,
  Error as ErrorIcon,
  EventNote as EventIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { CARD_COLORS } from './account.constants';

interface DeletionBlocker {
  type: string;
  message: string;
  details?: Record<string, unknown>;
}

interface Props {
  blockers: DeletionBlocker[];
  canDelete: boolean;
  isFunMode: boolean;
  onOpenDeleteDialog: () => void;
}

export default function DeletionSection({
  blockers,
  canDelete,
  isFunMode,
  onOpenDeleteDialog,
}: Props) {
  const getBlockerIcon = (type: string) => {
    switch (type) {
      case 'OUTSTANDING_BALANCE':
        return <ReceiptIcon color="error" />;
      case 'PENDING_BOOKINGS':
        return <EventIcon color="error" />;
      case 'ACTIVE_LESSONS':
        return <SchoolIcon color="error" />;
      case 'ONLY_ADMIN':
        return <PersonIcon color="error" />;
      default:
        return <ErrorIcon color="error" />;
    }
  };

  return (
    <Card
      sx={{
        border: '1px solid',
        borderColor: 'error.light',
        ...(isFunMode && {
          borderRadius: 4,
          border: `3px solid ${CARD_COLORS.delete.border}`,
          bgcolor: CARD_COLORS.delete.bg,
          boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
        }),
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              bgcolor: isFunMode ? `${CARD_COLORS.delete.border}30` : 'error.light',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isFunMode ? CARD_COLORS.delete.border : 'error.main',
              flexShrink: 0,
            }}
          >
            <DeleteIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" color="error">
              Delete Account
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Permanently delete your account and all associated data. This action cannot be
              undone after the 30-day grace period.
            </Typography>

            {blockers.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  You cannot delete your account at this time:
                </Typography>
                <List dense disablePadding>
                  {blockers.map((blocker, index) => (
                    <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {getBlockerIcon(blocker.type)}
                      </ListItemIcon>
                      <ListItemText primary={blocker.message} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={onOpenDeleteDialog}
              disabled={!canDelete}
            >
              Request Account Deletion
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
